<template>

    <vue-nav-bar></vue-nav-bar>

    <div class="py-4 mainContainer">

        <div class="navbar-container">
            <!-- Progress Line -->
            <div class="navbar-progress">
                <div class="navbar-fill" :style="{ width: progressWidth }"></div>
            </div>

            <!-- Steps -->
            <div v-for="(step, index) in steps" :key="index"
                :class="['step', { active: index === currentStep, completed: index < currentStep }]">
                <div class="circle">
                    <i :class="step.icon"></i>
                </div>
                <p>{{ step.label }}</p>
            </div>
        </div>

        <div class="form-tab shadow-sm bg-body-tertiary" ref="stepContainer" id="step-container">
            <!-- Personal Info Tab -->
            <div v-if="currentStep === 0">
                <h4 style="margin-bottom: 30px; font-weight: bold;">{{ $Translate('personalInformationTitle') }}
                </h4>

                <!-- Get Personal Information Form -->
                <PersonalInfo :personalInfo="personalInfo" :personalSubmitted="personalSubmitted"
                    :countryList="countryList" :genderList="genderList" :nameIsValid="nameIsValid"
                    :arabicNameIsValid="arabicNameIsValid" :nationalIdIsValid="nationalIdIsValid"
                    :countryRegionIdIsValid="countryRegionIdIsValid" :genderIsValid="genderIsValid"
                    :dateOfBirthIsValid="dateOfBirthIsValid" :emailIsValid="emailIsValid"
                    :confirmEmailIsValid="confirmEmailIsValid" :phoneIsValid="phoneIsValid"
                    :passwordIsValid="passwordIsValid" :confirmPasswordIsValid="confirmPasswordIsValid"
                    :linkedInLinkIsValid="linkedInLinkIsValid" :yearOfExperienceIsValid="yearOfExperienceIsValid" />

                <div class="row">
                    <hr>
                    <div class="buttons-container" style="display: flex; justify-content: end;" v-if="!isRTL">
                        <button type="button" class="next-btn" @click.prevent="next()">{{ $Translate('nextBtnText')
                            }}<i class="fa-solid fa-angles-right"></i></button>
                    </div>

                    <div class="buttons-container" style="display: flex; justify-content: start;" v-if="isRTL">
                        <button class="next-btn" @click="next()">{{ $Translate('nextBtnText') }}<i
                                class="fa-solid fa-angles-left"></i></button>
                    </div>
                </div>
            </div>

            <!-- Education Tab -->
            <div v-if="currentStep === 1">
                <h4 style="margin-bottom: 30px; font-weight: bold;">{{ $Translate('educationTitle') }}</h4>

                <!-- Get Education Form -->
                <Education :education="education" :eduSubmitted="eduSubmitted" :educationList="educationList"
                    :educationalLevelsList="educationalLevelsList"
                    :educationDisciplineIsValid="educationDisciplineIsValid"
                    :educationInstituteIsValid="educationInstituteIsValid"
                    :educationLevelIsValid="educationLevelIsValid" :gradePointIsValid="gradePointIsValid"
                    :startDateIsValid="startDateIsValid" :endDateIsValid="endDateIsValid"
                    :descriptionIsValid="descriptionIsValid" />

                <!-- Add Education Button -->
                <div style="display: flex; justify-content: end;" v-if="!isRTL">
                    <button type="button" class="add-btn mb-4 mt-3" @click.prevent="addEducation()">{{
                        $Translate('addBtnText')
                        }}<i class="fa-solid fa-plus"></i></button>
                </div>

                <!-- Education Table-->
                <div class="table-responsive" v-if="educationList.length > 0">
                    <table class="table table-striped align-middle text-center">
                        <thead class="table-light">
                            <tr>
                                <th>#</th>
                                <th>{{ $Translate('educationDiscipline') }}</th>
                                <th>{{ $Translate('educationInstitute') }}</th>
                                <th>{{ $Translate('educationLevel') }}</th>
                                <th>{{ $Translate('gradePoint') }}</th>
                                <th>{{ $Translate('startDate') }}</th>
                                <th>{{ $Translate('endDate') }}</th>
                                <th>{{ $Translate('description') }}</th>
                                <th>{{ $Translate('action') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in educationList" :key="index">
                                <th scope="row">{{ index + 1 }}</th>
                                <td>{{ item.educationDiscipline }}</td>
                                <td>{{ item.educationInstitute }}</td>
                                <td>{{ item.educationalLevelDesc }}</td>
                                <td>{{ item.gradePoint }}</td>
                                <td>{{ item.startDate }}</td>
                                <td>{{ item.endDate }}</td>
                                <td :title="item.description"
                                    style="max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                                    {{ item.description }}</td>
                                <td>
                                    <button type="button" class="btn btn-sm btn-danger" @click="deleteEducation(index)">
                                        <i class="fa fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="row">
                    <hr>
                    <div class="buttons-container" style="display: flex; justify-content: space-between;" v-if="!isRTL">
                        <button class="back-btn" @click="back()"><i class="fa-solid fa-angles-left"></i>{{
                            $Translate('backBtnText') }}</button>
                        <button type="button" class="next-btn" @click="next()">{{ $Translate('nextBtnText') }}<i
                                class="fa-solid fa-angles-right"></i></button>
                    </div>

                    <div class="buttons-container" style="display: flex; justify-content: space-between;" v-if="isRTL">
                        <button class="back-btn" @click="back()"><i class="fa-solid fa-angles-right"></i>{{
                            $Translate('backBtnText') }}</button>
                        <button type="button" class="next-btn" @click="next()">{{ $Translate('nextBtnText') }}<i
                                class="fa-solid fa-angles-left"></i></button>
                    </div>
                </div>

            </div>

            <!-- Skills Tab -->
            <div v-if="currentStep === 2">
                <h4 style="margin-bottom: 30px; font-weight: bold;">{{ $Translate('skillsTitle') }}</h4>

                <!-- Get Skills Form -->
                <Skills :skill="skill" :skillSubmitted="skillSubmitted" :skillList="skillList" :skillsList="skillsList"
                    :ratingLevelList="ratingLevelList" :skillIdIsValid="skillIdIsValid"
                    :ratingLevelIdIsValid="ratingLevelIdIsValid" @skill-changed="GetSkillRating" />

                <!-- Add Skill Button -->
                <div style="display: flex; justify-content: end;" v-if="!isRTL">
                    <button type="button" class="add-btn mb-4 mt-3" @click.prevent="addSkill()">{{
                        $Translate('addBtnText')
                        }}<i class="fa-solid fa-plus"></i></button>
                </div>

                <!-- Skill Table-->
                <div class="table-responsive" v-if="skillList.length > 0">
                    <table class="table table-striped align-middle text-center">
                        <thead class="table-light">
                            <tr>
                                <th>#</th>
                                <th>{{ $Translate('skill') }}</th>
                                <th>{{ $Translate('ratingLevel') }}</th>
                                <th>{{ $Translate('action') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in skillList" :key="index">
                                <th scope="row">{{ index + 1 }}</th>
                                <td>{{ item.description }}</td>
                                <td>{{ item.ratingLevelDescription }}</td>
                                <td>
                                    <button type="button" class="btn btn-sm btn-danger" @click="deleteSkill(index)">
                                        <i class="fa fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="row">
                    <hr>
                    <div class="buttons-container" style="display: flex; justify-content: space-between;" v-if="!isRTL">
                        <button class="back-btn" @click="back()"><i class="fa-solid fa-angles-left"></i>{{
                            $Translate('backBtnText') }}</button>
                        <button type="button" class="next-btn" @click="next()">{{ $Translate('nextBtnText') }}<i
                                class="fa-solid fa-angles-right"></i></button>
                    </div>

                    <div class="buttons-container" style="display: flex; justify-content: space-between;" v-if="isRTL">
                        <button class="back-btn" @click="back()"><i class="fa-solid fa-angles-right"></i>{{
                            $Translate('backBtnText') }}</button>
                        <button type="button" class="next-btn" @click="next()">{{ $Translate('nextBtnText') }}<i
                                class="fa-solid fa-angles-left"></i></button>
                    </div>
                </div>
            </div>

            <!-- Professional Experience Tab -->
            <div v-if="currentStep === 3">
                <h4 style="margin-bottom: 30px; font-weight: bold;">{{ $Translate('professionalExperienceTitle') }}
                </h4>

                <ProfessionalExperience :professionalExperience="professionalExperience"
                    :profExpSubmitted="profExpSubmitted" :professionalExperienceList="professionalExperienceList"
                    :isChecked="isChecked" :employmentTypeIsValid="employmentTypeIsValid"
                    :employerPositionIsValid="employerPositionIsValid" :nameOfEmployerIsValid="nameOfEmployerIsValid"
                    :startDateProfessionalExperienceIsValid="startDateProfessionalExperienceIsValid"
                    :endDateProfessionalExperienceIsValid="endDateProfessionalExperienceIsValid"
                    :descriptionProfessionalExperienceIsValid="descriptionProfessionalExperienceIsValid"
                    @toggle-check="toggleCheck" />

                <!-- Add Professional Experience Button -->
                <div style="display: flex; justify-content: end;" v-if="!isRTL">
                    <button type="button" class="add-btn mb-4 mt-3" @click.prevent="addProfessionalExperience()">{{
                        $Translate('addBtnText')
                        }}<i class="fa-solid fa-plus"></i></button>
                </div>

                <!-- Professional Experience Table-->
                <div class="table-responsive" v-if="professionalExperienceList.length > 0">
                    <table class="table table-striped align-middle text-center">
                        <thead class="table-light">
                            <tr>
                                <th>#</th>
                                <th>{{ $Translate('employmentType') }}</th>
                                <th>{{ $Translate('employerPosition') }}</th>
                                <th>{{ $Translate('nameOfEmployer') }}</th>
                                <th>{{ $Translate('startDate') }}</th>
                                <th>{{ $Translate('endDate') }}</th>
                                <th>{{ $Translate('description') }}</th>
                                <th>{{ $Translate('action') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in professionalExperienceList" :key="index">
                                <th scope="row">{{ index + 1 }}</th>
                                <td>{{ item.employment }}</td>
                                <td>{{ item.employerPosition }}</td>
                                <td>{{ item.nameOfEmployer }}</td>
                                <td>{{ item.startDateProfessionalExperience }}</td>
                                <td>{{ item.endDateProfessionalExperience === null ? $Translate('present') :
                                    item.endDateProfessionalExperience }}</td>
                                <td :title="item.descriptionProfessionalExperience"
                                    style="max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                                    {{ item.descriptionProfessionalExperience }}</td>
                                <td>
                                    <button type="button" class="btn btn-sm btn-danger"
                                        @click="deleteProfessionalExperience(index)">
                                        <i class="fa fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="row">
                    <hr>
                    <div class="buttons-container" style="display: flex; justify-content: space-between;" v-if="!isRTL">
                        <button class="back-btn" @click="back()"><i class="fa-solid fa-angles-left"></i>{{
                            $Translate('backBtnText') }}</button>
                        <button type="button" class="next-btn" @click="next()">{{ $Translate('nextBtnText') }}<i
                                class="fa-solid fa-angles-right"></i></button>
                    </div>

                    <div class="buttons-container" style="display: flex; justify-content: space-between;" v-if="isRTL">
                        <button class="back-btn" @click="back()"><i class="fa-solid fa-angles-right"></i>{{
                            $Translate('backBtnText') }}</button>
                        <button type="button" class="next-btn" @click="next()">{{ $Translate('nextBtnText') }}<i
                                class="fa-solid fa-angles-left"></i></button>
                    </div>

                </div>
            </div>

            <!-- Certificates Tab -->
            <div v-if="currentStep === 4">
                <h4 style="margin-bottom: 30px; font-weight: bold;">{{ $Translate('certificatesTitle') }}</h4>

                <Certificates :certificate="certificate" :certSubmitted="certSubmitted"
                    :certificateList="certificateList" :certificateTypeIdIsValid="certificateTypeIdIsValid"
                    :unlistedCertificateIsValid="unlistedCertificateIsValid"
                    :startDateCertificateIsValid="startDateCertificateIsValid"
                    :endDateCertificateIsValid="endDateCertificateIsValid"
                    :descriptionCertificateIsValid="descriptionCertificateIsValid"
                    :certificateFileIsValid="certificateFileIsValid" :certificateURLIsValid="certificateURLIsValid"
                    :certificateLinkIsValid="certificateLinkIsValid"
                    :certificateUploadTypeIsValid="certificateUploadTypeIsValid" :certificatesList="certificatesList"
                    @certificate-file-uploaded="handleCertificateFile" ref="certificateForm" />

                <!-- Add Certificate Button -->
                <div style="display: flex; justify-content: end;" v-if="!isRTL">
                    <button type="button" class="add-btn mb-4 mt-3" @click.prevent="addCertificate()">{{
                        $Translate('addBtnText')
                        }}<i class="fa-solid fa-plus"></i></button>
                </div>

                <!-- Certificate Table-->
                <div class="table-responsive" v-if="certificateList.length > 0">
                    <table class="table table-striped align-middle text-center">
                        <thead class="table-light">
                            <tr>
                                <th>{{ $Translate('certificate') }}</th>
                                <th>{{ $Translate('unlistedCertificate') }}</th>
                                <th>{{ $Translate('startDate') }}</th>
                                <th>{{ $Translate('endDate') }}</th>
                                <th>{{ $Translate('description') }}</th>
                                <th>{{ $Translate('action') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in certificateList" :key="index">
                                <td>{{ item.certificateName }}</td>
                                <td>{{ item.unlistedCertificate }}</td>
                                <td>{{ item.startDateCertificate }}</td>
                                <td>{{ item.endDateCertificate }}</td>
                                <td :title="item.descriptionCertificate"
                                    style="max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                                    {{ item.descriptionCertificate }}</td>
                                <td>
                                    <button type="button" class="btn btn-sm btn-danger"
                                        @click="deleteCertificate(index)">
                                        <i class="fa fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="row">
                    <hr>
                    <div class="buttons-container" style="display: flex; justify-content: space-between;" v-if="!isRTL">
                        <button class="back-btn" @click="back()"><i class="fa-solid fa-angles-left"></i>{{
                            $Translate('backBtnText') }}</button>
                        <button type="button" class="next-btn" @click="next()">{{ $Translate('nextBtnText') }}<i
                                class="fa-solid fa-angles-right"></i></button>
                    </div>

                    <div class="buttons-container" style="display: flex; justify-content: space-between;" v-if="isRTL">
                        <button class="back-btn" @click="back()"><i class="fa-solid fa-angles-right"></i>{{
                            $Translate('backBtnText') }}</button>
                        <button type="button" class="next-btn" @click="next()">{{ $Translate('nextBtnText') }}<i
                                class="fa-solid fa-angles-left"></i></button>
                    </div>
                </div>
            </div>

            <!-- Courses Tab -->
            <div v-if="currentStep === 5">
                <h4 style="margin-bottom: 30px; font-weight: bold;">{{ $Translate('coursesTitle') }}</h4>

                <Courses :course="course" :courseSubmitted="courseSubmitted" :courseList="courseList"
                    :courseNameIsValid="courseNameIsValid" :startDateCourseIsValid="startDateCourseIsValid"
                    :endDateCourseIsValid="endDateCourseIsValid" :descriptionCourseIsValid="descriptionCourseIsValid" />

                <!-- Add Course Button -->
                <div style="display: flex; justify-content: end;" v-if="!isRTL">
                    <button type="button" class="add-btn mb-4 mt-3" @click.prevent="addCourse()">{{
                        $Translate('addBtnText')
                        }}<i class="fa-solid fa-plus"></i></button>
                </div>

                <!-- Course Table-->
                <div class="table-responsive" v-if="courseList.length > 0">
                    <table class="table table-striped align-middle text-center">
                        <thead class="table-light">
                            <tr>
                                <th>{{ $Translate('courseName') }}</th>
                                <th>{{ $Translate('startDate') }}</th>
                                <th>{{ $Translate('endDate') }}</th>
                                <th>{{ $Translate('description') }}</th>
                                <th>{{ $Translate('action') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in courseList" :key="index">
                                <td>{{ item.courseName }}</td>
                                <td>{{ item.startDateCourse }}</td>
                                <td>{{ item.endDateCourse }}</td>
                                <td :title="item.descriptionCourse"
                                    style="max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                                    {{ item.descriptionCourse }}</td>
                                <td>
                                    <button type="button" class="btn btn-sm btn-danger" @click="deleteCourse(index)">
                                        <i class="fa fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="row">
                    <hr>
                    <div class="buttons-container" style="display: flex; justify-content: space-between;" v-if="!isRTL">
                        <button class="back-btn" @click="back()"><i class="fa-solid fa-angles-left"></i>{{
                            $Translate('backBtnText') }}</button>
                        <button type="button" class="next-btn" @click="next()">{{ $Translate('nextBtnText') }}<i
                                class="fa-solid fa-angles-right"></i></button>
                    </div>

                    <div class="buttons-container" style="display: flex; justify-content: space-between;" v-if="isRTL">
                        <button class="back-btn" @click="back()"><i class="fa-solid fa-angles-right"></i>{{
                            $Translate('backBtnText') }}</button>
                        <button type="button" class="next-btn" @click="next()">{{ $Translate('nextBtnText') }}<i
                                class="fa-solid fa-angles-left"></i></button>
                    </div>
                </div>
            </div>

            <!-- Project Experience Tab -->
            <div v-if="currentStep === 6">
                <h4 style="margin-bottom: 30px; font-weight: bold;">{{ $Translate('projectExperienceTitle') }}</h4>

                <ProjectExperience :projectExperience="projectExperience" :projExpSubmitted="projExpSubmitted"
                    :projectExperienceList="projectExperienceList" :roleIsValid="roleIsValid"
                    :startDateProjectExperienceIsValid="startDateProjectExperienceIsValid"
                    :endDateProjectExperienceIsValid="endDateProjectExperienceIsValid"
                    :descriptionProjectExperienceIsValid="descriptionProjectExperienceIsValid" />


                <!-- Add Project Experience Button -->
                <div style="display: flex; justify-content: end;" v-if="!isRTL">
                    <button type="button" class="add-btn mb-4 mt-3" @click.prevent="addProjectExperience()">{{
                        $Translate('addBtnText')
                        }}<i class="fa-solid fa-plus"></i></button>
                </div>

                <!-- Project Experience Table-->
                <div class="table-responsive" v-if="projectExperienceList.length > 0">
                    <table class="table table-striped align-middle text-center">
                        <thead class="table-light">
                            <tr>
                                <th>{{ $Translate('role') }}</th>
                                <th>{{ $Translate('startDate') }}</th>
                                <th>{{ $Translate('endDate') }}</th>
                                <th>{{ $Translate('description') }}</th>
                                <th>{{ $Translate('action') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in projectExperienceList" :key="index">
                                <td>{{ item.role }}</td>
                                <td>{{ item.startDateProjectExperience }}</td>
                                <td>{{ item.endDateProjectExperience }}</td>
                                <td :title="item.descriptionProjectExperience"
                                    style="max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                                    {{ item.descriptionProjectExperience }}</td>
                                <td>
                                    <button type="button" class="btn btn-sm btn-danger"
                                        @click="deleteProjectExperience(index)">
                                        <i class="fa fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="row">
                    <hr>
                    <div class="buttons-container" style="display: flex; justify-content: space-between;" v-if="!isRTL">
                        <button class="back-btn" @click="back()"><i class="fa-solid fa-angles-left"></i>{{
                            $Translate('backBtnText') }}</button>
                        <button type="button" class="next-btn" @click="next()">{{ $Translate('nextBtnText') }}<i
                                class="fa-solid fa-angles-right"></i></button>
                    </div>

                    <div class="buttons-container" style="display: flex; justify-content: space-between;" v-if="isRTL">
                        <button class="back-btn" @click="back()"><i class="fa-solid fa-angles-right"></i>{{
                            $Translate('backBtnText') }}</button>
                        <button type="button" class="next-btn" @click="next()">{{ $Translate('nextBtnText') }}<i
                                class="fa-solid fa-angles-left"></i></button>
                    </div>
                </div>
            </div>

            <!-- Resume Tab -->
            <div v-if="currentStep === 7">
                <h4 style="margin-bottom: 30px; font-weight: bold;">{{ $Translate('resumeTitle') }}</h4>

                <Resume :resume="resume" :resumeSubmitted="resumeSubmitted" :resumeFileIsValid="resumeFileIsValid"
                    :resumeLinkIsValid="resumeLinkIsValid" :resumeUploadTypeIsValid="resumeUploadTypeIsValid"
                    @resume-file-uploaded="handleResumeFileUpload" ref="resumeForm" />

                <div class="row">
                    <hr>
                    <div class="buttons-container" style="display: flex; justify-content: space-between;" v-if="!isRTL">
                        <button class="back-btn" @click="back()"><i class="fa-solid fa-angles-left"></i>{{
                            $Translate('backBtnText') }}</button>
                        <button type="button" class="next-btn" @click.prevent="submitApplication()">{{
                            $Translate('submit')
                            }}<i class="fa-solid fa-paper-plane"></i></button>
                    </div>

                    <div class="buttons-container" style="display: flex; justify-content: space-between;" v-if="isRTL">
                        <button class="back-btn" @click="back()"><i class="fa-solid fa-angles-right"></i>{{
                            $Translate('backBtnText') }}</button>
                        <button type="button" class="next-btn" @click.prevent="submitApplication()">{{
                            $Translate('submit')
                            }}<i class="fa-solid fa-paper-plane"></i></button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Success Submission Modal -->
        <div class="modal fade" id="regSuccessModal" tabindex="-1" aria-labelledby="regSuccessModalLabel"
            aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content custom-success-modal text-center px-4 py-3 rounded-4">

                    <!-- 
                    <div class="modal-header border-0 p-0">
                        <button type="button" class="btn-close ms-auto" data-bs-dismiss="modal"
                            aria-label="Close"></button>
                    </div>
                    -->

                    <div class="modal-body">
                        <div class="success-icon mb-4">
                            <img :src="gifSource" alt="Animated GIF" class="img-fluid" />
                        </div>
                        <h4 class="fw-bold mb-2">{{ $Translate('successMessageTitle') }}</h4>
                        <p class="text-muted mb-4">{{ $Translate('successMessageBody') }}</p>

                        <div class="d-flex justify-content-center">
                            <button type="button" class="btn btn-primary px-4 py-2 rounded-3" data-bs-dismiss="modal"
                                @click="Restart">
                                {{ $Translate('close') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Failed Submission Modal -->
        <div class="modal fade" id="regFailedModal" tabindex="-1" aria-labelledby="regFailedModalLabel"
            aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content custom-success-modal text-center px-4 py-3 rounded-4">

                    <div class="modal-header border-0 p-0">
                        <button type="button" class="btn-close ms-auto" data-bs-dismiss="modal"
                            aria-label="Close"></button>
                    </div>

                    <div class="modal-body">
                        <div class="failed-icon mb-4">
                            <img :src="gifSource2" alt="Animated GIF" class="img-fluid" />
                        </div>
                        <h4 class="fw-bold mb-2">{{ $Translate('failedMessageTitle') }}</h4>
                        <p class="text-muted mb-4">{{ $Translate('failedMessageBody') }}</p>

                        <div class="d-flex justify-content-center">
                            <button type="button" class="btn btn-primary px-4 py-2 rounded-3" data-bs-dismiss="modal"
                                aria-label="Close">
                                {{ $Translate('close') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div>

    <vue-footer :project_name="'Applicant Portal.'"></vue-footer>

</template>

<script>
import PersonalInfo from '../RegistrationForms/PersonalInfo.vue';
import Education from '../RegistrationForms/Education.vue';
import Skills from '../RegistrationForms/Skills.vue';
import ProfessionalExperience from '../RegistrationForms/ProfessionalExperience.vue';
import Certificates from '../RegistrationForms/Certificates.vue';
import Courses from '../RegistrationForms/Courses.vue';
import ProjectExperience from '../RegistrationForms/ProjectExperience.vue';
import Resume from '../RegistrationForms/Resume.vue';
import { Modal } from 'bootstrap';
import { runWithoutBlocking } from '../../VueGlobal';

let regSuccessModal = null;
let regFailedModal = null;

export default {
    name: 'RegistrationPage',
    components: {
        PersonalInfo,
        Education,
        Skills,
        ProfessionalExperience,
        Certificates,
        Courses,
        ProjectExperience,
        Resume
    },
    data() {
        return {
            currentStep: 0,
            steps: [
                { label: this.$Translate('personalInformationTitle'), icon: "fas fa-id-card" },
                { label: this.$Translate('educationTitle'), icon: "fas fa-graduation-cap" },
                { label: this.$Translate('skillsTitle'), icon: "fas fa-award" },
                { label: this.$Translate('professionalExperienceTitle'), icon: "fas fa-briefcase" },
                { label: this.$Translate('certificatesTitle'), icon: "fas fa-scroll" },
                { label: this.$Translate('coursesTitle'), icon: "fas fa-book-open" },
                { label: this.$Translate('projectExperienceTitle'), icon: "fas fa-project-diagram" },
                { label: this.$Translate('resumeTitle'), icon: "fas fa-file-alt" }
            ],
            companyId: 'usmf',
            applicantId: GetApplicantId(),
            token: GetToken(),
            isRTL: document.documentElement.dir === 'rtl', // for Ar version
            //submitted: false,
            gifSource: require('../../../assets/animations/success.gif'),
            gifSource2: require('../../../assets/animations/warning.gif'),

            personalSubmitted: false,
            eduSubmitted: false,
            skillSubmitted: false,
            profExpSubmitted: false,
            certSubmitted: false,
            courseSubmitted: false,
            projExpSubmitted: false,
            resumeSubmitted: false,

            grant_type: 'password',
            registration: true,

            applicantIsValid: false,

            // Personal Validation
            personalInfo: {
                name: '',
                arabicName: '',
                nationalId: '',
                countryRegionId: '',
                gender: '',
                dateOfBirth: '',
                email: '',
                confirmEmail: '',
                phone: '',
                password: '',
                confirmPassword: '',
                linkedInLink: '',
                yearOfExperience: 0
            },

            countryList: [],
            gender: '',
            countryRegionId: '',

            nameIsValid: false,
            arabicNameIsValid: false,
            nationalIdIsValid: false,
            countryRegionIdIsValid: false,
            genderIsValid: false,
            dateOfBirthIsValid: false,
            emailIsValid: false,
            confirmEmailIsValid: false,
            phoneIsValid: false,
            passwordIsValid: false,
            confirmPasswordIsValid: false,
            linkedInLinkIsValid: false,
            yearOfExperienceIsValid: false,

            // Education Validation
            education: {
                educationDiscipline: '',
                educationInstitute: '',
                educationLevel: '',
                gradePoint: '',
                startDate: '',
                endDate: '',
                description: '',
                educationalLevelDesc: ''
            },

            educationList: [],

            educationDisciplineIsValid: false,
            educationInstituteIsValid: false,
            educationLevelIsValid: false,
            gradePointIsValid: false,
            startDateIsValid: false,
            endDateIsValid: false,
            descriptionIsValid: false,

            // Skills Validation
            skill: {
                description: '',
                skillId: '',
                ratingLevelId: '',
                ratingLevelDescription: '',
                recId: null
            },
            skillIdIsValid: false,
            ratingLevelIdIsValid: false,

            skillList: [],

            // Professional Experience Validation
            professionalExperience: {
                employment: '',
                startDateProfessionalExperience: '',
                endDateProfessionalExperience: '',
                descriptionProfessionalExperience: '',
                employerPosition: '',
                nameOfEmployer: '',
            },
            professionalExperienceList: [],
            isChecked: false,

            employmentTypeIsValid: false,
            employerPositionIsValid: false,
            nameOfEmployerIsValid: false,
            startDateProfessionalExperienceIsValid: false,
            endDateProfessionalExperienceIsValid: false,
            descriptionProfessionalExperienceIsValid: false,

            // Certificates Validation
            certificate: {
                certificateName: '',
                certificateTypeId: '',
                unlistedCertificate: '',
                startDateCertificate: '',
                endDateCertificate: '',
                descriptionCertificate: '',
                certificateType: 'file',
                certificateLink: '',
                certificateFile: null
            },

            certificateList: [],

            certificateTypeIdIsValid: false,
            unlistedCertificateIsValid: false,
            startDateCertificateIsValid: false,
            endDateCertificateIsValid: false,
            descriptionCertificateIsValid: false,
            certificateFileIsValid: false,
            certificateURLIsValid: false,
            certificateLinkIsValid: false,
            certificateUploadTypeIsValid: false,

            // Courses Validation
            course: {
                courseName: '',
                startDateCourse: '',
                endDateCourse: '',
                descriptionCourse: ''
            },

            courseList: [],

            courseNameIsValid: false,
            startDateCourseIsValid: false,
            endDateCourseIsValid: false,
            descriptionCourseIsValid: false,

            // Project Experience Validation
            projectExperience: {
                role: '',
                startDateProjectExperience: '',
                endDateProjectExperience: '',
                descriptionProjectExperience: ''
            },

            projectExperienceList: [],

            roleIsValid: false,
            startDateProjectExperienceIsValid: false,
            endDateProjectExperienceIsValid: false,
            descriptionProjectExperienceIsValid: false,

            // Resume Validation
            resume: {
                resumeName: '',
                resumeType: 'file',
                resumeLink: '',
                resumeFile: null
            },

            resumeFileIsValid: false,
            resumeLinkIsValid: false,
            resumeUploadTypeIsValid: false,

            // For API's
            countryList: [],
            genderList: [],
            educationalLevelsList: [], // e.g. Bsc., Diploma , etc.
            educationsList: [],
            skillsList: [],
            certificatesList: [],
            ratingLevelList: [],
        };
    },
    methods: {
        async next() {
            //this.submitted = true;

            const isValid = await this.validateCurrentStep();
            if (this.currentStep === 0) {
                if (isValid) {
                    this.animateStep('next');
                    this.previousStep = this.currentStep;

                    setTimeout(() => {
                        this.currentStep++;
                    }, 300);
                }
            } else {
                if (this.currentStep < this.steps.length - 1) {
                    this.animateStep('next');
                    this.previousStep = this.currentStep;

                    setTimeout(() => {
                        this.currentStep++;
                    }, 300);
                }
            }
        },
        back() {
            if (this.currentStep > 0) {
                this.animateStep('back');
                this.previousStep = this.currentStep;

                setTimeout(() => {
                    this.currentStep--;
                }, 300);
            }
        },
        GetGenders: async function () {
            try {
                this.genderList = await GetLookUpGender();
            } catch (error) {
                console.error("Error fetching genders:", error);
            }
        },
        GetCountries: async function () {
            try {
                this.countryList = await GetLookUpCountryId(this.companyId);
            } catch (error) {
                console.error("Error fetching countries:", error);
            }
        },
        GetEducationLevel: async function () {
            try {
                this.educationalLevelsList = await GetLookUpEducationLevel(this.companyId);
            } catch (error) {
                console.error("Error fetching educational levels:", error);
            }
        },
        GetEducation: async function () {
            try {
                this.educationsList = await GetLookUpEducation(this.companyId);
            } catch (error) {
                console.error("Error fetching education:", error);
            }
        },
        GetSkill: async function () {
            try {
                this.skillsList = await GetLookUpSkill(this.companyId);
            } catch (error) {
                console.error("Error fetching skills:", error);
            }
        },
        GetSkillRating: async function () {
            try {
                this.ratingLevelList = await GetLookUpSkillRatingLevel(this.companyId, this.skill.recId);
            } catch (error) {
                console.error("Error fetching skill rating levels:", error);
            }
        },
        GetCertificate: async function () {
            try {
                this.certificatesList = await GetLookUpCertificateType(this.companyId);
            } catch (error) {
                console.error("Error fetching certificates:", error);
            }
        },
        async ValidateApplicant() {
            try {
                const result = await ValidateApplicant(
                    this.companyId,
                    this.personalInfo.name,
                    this.personalInfo.arabicName,
                    this.personalInfo.nationalId,
                    this.personalInfo.countryRegionId,
                    this.personalInfo.gender,
                    this.personalInfo.dateOfBirth,
                    this.personalInfo.email,
                    this.personalInfo.confirmEmail,
                    this.personalInfo.phone,
                    this.personalInfo.password,
                    this.personalInfo.confirmPassword,
                    this.personalInfo.linkedInLink,
                    this.personalInfo.yearOfExperience
                );

                const responseData = result?.data ?? result;

                if (typeof responseData === 'string' && responseData.trim() === '') {
                    this.applicantIsValid = true;
                    toastr.success(this.$Translate('applicantValidated'));
                    this.personalSubmitted = false;
                    return true;
                } else {
                    this.applicantIsValid = false;
                    this.personalSubmitted = true;
                    return false;
                }
            } catch (error) {
                console.error("Applicant validation failed:", error);
                this.applicantIsValid = false;
                return false;
            }
        },
        async validatePersonalInfoStep() {
            let valid = true;
            this.personalSubmitted = true;

            // Name
            if (!this.personalInfo.name) {
                toastr.error(this.$Translate('nameRequired'));
                this.nameIsValid = false;
                valid = false;
            } else if (this.personalInfo.name.length < 8) {
                toastr.warning(this.$Translate('nameTooShort'));
                this.nameIsValid = false;
                valid = false;
            } else if (/[^a-zA-Z\s]/.test(this.personalInfo.name)) {
                toastr.warning(this.$Translate('nameInvalidChars'));
                this.nameIsValid = false;
                valid = false;
            } else {
                this.nameIsValid = true;
            }

            this.arabicNameIsValid = true;
            this.nationalIdIsValid = true;

            // Country
            if (!this.personalInfo.countryRegionId) {
                toastr.error(this.$Translate('countryRequired'));
                this.countryRegionIdIsValid = false;
                valid = false;
            } else {
                this.countryRegionIdIsValid = true;
            }

            // Gender
            if (!this.personalInfo.gender) {
                toastr.error(this.$Translate('genderRequired'));
                this.genderIsValid = false;
                valid = false;
            } else {
                this.genderIsValid = true;
            }

            // Date of Birth
            if (!this.personalInfo.dateOfBirth) {
                toastr.error(this.$Translate('dateOfBirthRequired'));
                this.dateOfBirthIsValid = false;
                valid = false;
            } else {
                this.dateOfBirthIsValid = true;
            }

            // Email
            if (!this.personalInfo.email) {
                toastr.error(this.$Translate('emailRequired'));
                this.emailIsValid = false;
                valid = false;
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.personalInfo.email)) {
                toastr.warning(this.$Translate('invalidEmail'));
                this.emailIsValid = false;
                valid = false;
            } else {
                this.emailIsValid = true;
            }

            // Confirm Email
            if (!this.personalInfo.confirmEmail || this.personalInfo.confirmEmail.trim() === '') {
                toastr.error(this.$Translate('confirmEmailRequired'));
                this.confirmEmailIsValid = false;
                valid = false;
            } else if (this.personalInfo.confirmEmail !== this.personalInfo.email) {
                toastr.warning(this.$Translate('confirmEmailMismatch'));
                this.confirmEmailIsValid = false;
                valid = false;
            } else {
                this.confirmEmailIsValid = true;
            }

            // Phone
            if (!this.personalInfo.phone) {
                toastr.error(this.$Translate('phoneRequired'));
                this.phoneIsValid = false;
                valid = false;
            } else {
                this.phoneIsValid = true;
            }

            // Password
            if (!this.personalInfo.password) {
                toastr.error(this.$Translate('passwordRequired'));
                this.passwordIsValid = false;
                valid = false;
            } else if (this.personalInfo.password.length < 8) {
                toastr.warning(this.$Translate('passwordTooShort'));
                this.passwordIsValid = false;
                valid = false;
            } else if (!/[A-Z]/.test(this.personalInfo.password)) {
                toastr.warning(this.$Translate('passwordUppercaseRequired'));
                this.passwordIsValid = false;
                valid = false;
            } else if (!/[0-9]/.test(this.personalInfo.password)) {
                toastr.warning(this.$Translate('passwordNumberRequired'));
                this.passwordIsValid = false;
                valid = false;
            } else {
                this.passwordIsValid = true;
            }

            // Confirm Password
            if (!this.personalInfo.confirmPassword || this.personalInfo.confirmPassword.trim() === '') {
                toastr.error(this.$Translate('confirmPasswordRequired'));
                this.confirmPasswordIsValid = false;
                valid = false;
            } else if (this.personalInfo.confirmPassword !== this.personalInfo.password) {
                toastr.warning(this.$Translate('confirmPasswordMismatch'));
                this.confirmPasswordIsValid = false;
                valid = false;
            } else {
                this.confirmPasswordIsValid = true;
            }

            // LinkedIn
            if (this.personalInfo.linkedInLink && !/^https?:\/\/(www\.)?linkedin\.com\/.*$/.test(this.personalInfo.linkedInLink)) {
                toastr.warning(this.$Translate('invalidLinkedIn'));
                this.linkedInLinkIsValid = false;
                valid = false;
            } else {
                this.linkedInLinkIsValid = true;
            }

            // Years of Experience
            if (
                this.personalInfo.yearOfExperience === null ||
                this.personalInfo.yearOfExperience === '' ||
                isNaN(this.personalInfo.yearOfExperience)
            ) {
                toastr.error(this.$Translate('experienceRequired'));
                this.yearOfExperienceIsValid = false;
                valid = false;
            } else if (this.personalInfo.yearOfExperience < 0) {
                toastr.warning(this.$Translate('experienceInvalid'));
                this.yearOfExperienceIsValid = false;
                valid = false;
            } else {
                this.yearOfExperienceIsValid = true;
            }

            if (valid) {
                const isApplicantValid = await this.ValidateApplicant();
                if (!isApplicantValid) {
                    valid = false;
                }
            }

            return valid;
        },
        validateResumeStep() {
            let valid = true;
            this.resumeSubmitted = true;

            if (this.resume.resumeType === 'file') {
                if (!this.resume.resumeFile) {
                    toastr.error(this.$Translate('resumeFileRequired'));
                    this.resumeUploadTypeIsValid = false;
                    valid = false;
                } else {
                    this.resumeUploadTypeIsValid = true;
                }
            } else if (this.resume.resumeType === 'url') {
                if (!this.resume.resumeLink) {
                    toastr.error(this.$Translate('resumeUrlRequired'));
                    this.resumeUploadTypeIsValid = false;
                    valid = false;
                } else {
                    this.resumeUploadTypeIsValid = true;
                }
            }

            return valid;
        },
        async validateCurrentStep() {
            if (this.currentStep === 0) {
                return await this.validatePersonalInfoStep();
            }

            if (this.currentStep === 7) {
                return this.validateResumeStep();
            }

            return true;
        },
        addEducation() {
            let valid = true;
            this.eduSubmitted = true;

            this.gradePointIsValid = true;
            this.startDateIsValid = true;
            this.descriptionIsValid = true;

            // Education Discipline
            if (!this.education.educationDiscipline) {
                toastr.error(this.$Translate('educationDisciplineRequired'));
                this.educationDisciplineValid = false;
                valid = false;
            } else {
                this.educationDisciplineIsValid = true;
            }

            // Education Institute
            if (!this.education.educationInstitute) {
                toastr.error(this.$Translate('educationInstituteRequired'));
                this.educationInstituteIsValid = false;
                valid = false;
            } else {
                this.educationInstituteIsValid = true;
            }

            // Education Level
            if (!this.education.educationLevel) {
                toastr.error(this.$Translate('educationLevelRequired'));
                this.educationLevelIsValid = false;
                valid = false;
            } else {
                this.educationLevelIsValid = true;
            }

            // Education End Date
            if (!this.education.endDate) {
                toastr.error(this.$Translate('endDateRequired'));
                this.endDateIsValid = false;
                valid = false;
            } else {
                this.endDateIsValid = true;
            }

            if (valid) {
                // Add new education entry
                this.educationList.push({ ...this.education });

                this.eduSubmitted = false;

                //console.log('=== Education ===');
                //console.log(JSON.stringify(this.education, null, 2));
                //console.log('=== Education List ===');
                //console.log(JSON.stringify(this.educationList, null, 2));

                // Reset form
                this.education = {
                    educationDiscipline: '',
                    educationInstitute: '',
                    educationLevel: '',
                    gradePoint: '',
                    startDate: '',
                    endDate: '',
                    description: ''
                };
            }
        },
        CreateApplicantEducation: async function () {
            try {
                for (const edu of this.educationList) {
                    const result = await CreateApplicantEducation(
                        this.companyId,
                        this.applicantId,
                        edu.educationDiscipline,
                        edu.educationInstitute,
                        edu.educationLevel,
                        Number(edu.gradePoint),
                        edu.startDate,
                        edu.endDate,
                        edu.description
                    );
                    console.log("Education created:", result);
                }
            } catch (error) {
                console.error("Education creation failed:", error);
            }
        },
        deleteEducation(index) {
            this.educationList.splice(index, 1);
        },
        addSkill() {
            let valid = true;
            this.skillSubmitted = true;

            // Skill
            if (!this.skill.skillId) {
                toastr.error(this.$Translate('skillRequired'));
                this.skillIdIsValid = false;
                valid = false;
            } else {
                this.skillIdIsValid = true;
            }

            // Rating Level
            if (!this.skill.ratingLevelId && this.skill.skillId) {
                toastr.error(this.$Translate('ratingLevelIdRequired'));
                this.ratingLevelIdIsValid = false;
                valid = false;
            } else {
                this.ratingLevelIdIsValid = true;
            }
            if (valid) {
                // Add new education entry
                this.skillList.push({ ...this.skill });

                this.skillSubmitted = false;

                //console.log('=== Skills ===');
                //console.log(JSON.stringify(this.skill, null, 2));
                //console.log('=== Skill List ===');
                //console.log(JSON.stringify(this.skillList, null, 2));

                // Reset form
                this.skill = {
                    skillId: '',
                    ratingLevelId: ''
                };
            }
        },
        CreateApplicantSkill: async function () {
            try {
                for (const sk of this.skillList) {
                    const result = await CreateApplicantSkill(
                        this.companyId,
                        this.applicantId,
                        sk.recId,
                        sk.ratingLevelId
                    );
                    console.log("Skill created successfully: " + result);
                }
            } catch (error) {
                console.error("Skill creation failed:", error);
            }
        },
        deleteSkill(index) {
            this.skillList.splice(index, 1);
        },
        toggleCheck(checked) {
            this.isChecked = checked;

            if (checked) {
                this.professionalExperience.endDateProfessionalExperience = null;
            } else {
                this.professionalExperience.endDateProfessionalExperience = '';
            }
        },
        addProfessionalExperience() {
            let valid = true;
            this.profExpSubmitted = true;

            this.startDateProfessionalExperienceIsValid = true;
            this.descriptionProfessionalExperienceIsValid = true;

            // Employment Type
            if (!this.professionalExperience.employment) {
                toastr.error(this.$Translate('employmentRequired'));
                this.employmentTypeIsValid = false;
                valid = false;
            } else {
                this.employmentTypeIsValid = true;
            }

            // Position & Unemployment Type
            if (!this.professionalExperience.employerPosition) {
                const employment = this.professionalExperience.employment;

                if (employment === 'Employee') {
                    toastr.error(this.$Translate('employerPositionRequired'));
                    this.employerPositionIsValid = false;
                    valid = false;
                } else if (employment === 'Unemployed') {
                    toastr.error(this.$Translate('unEmploymentTypeRequired'));
                    this.employerPositionIsValid = false;
                    valid = false;
                }
            } else {
                this.employerPositionIsValid = true;
            }

            // Employer Name
            if (this.professionalExperience.employment !== 'Unemployed') {
                if (!this.professionalExperience.nameOfEmployer) {
                    toastr.error(this.$Translate('nameOfEmployerRequired'));
                    this.nameOfEmployerIsValid = false;
                    valid = false;
                } else {
                    this.nameOfEmployerIsValid = true;
                }
            } else if (this.professionalExperience.employment === 'Unemployed') {
                this.professionalExperience.nameOfEmployer = this.professionalExperience.employment;
            }

            // End Date
            if (!this.isChecked) {
                if (!this.professionalExperience.endDateProfessionalExperience) {
                    toastr.error(this.$Translate('endDateRequired'));
                    this.endDateProfessionalExperienceIsValid = false;
                    valid = false;
                } else {
                    this.endDateProfessionalExperienceIsValid = true;
                }
            } else {
                this.endDateProfessionalExperienceIsValid = true;
            }

            if (valid) {
                // Add new Professional Experience entry
                this.professionalExperienceList.push({ ...this.professionalExperience });

                this.profExpSubmitted = false;

                //('=== Professional Experience ===');
                //console.log(JSON.stringify(this.professionalExperience, null, 2));
                //console.log('=== Professional Experience List ===');
                //console.log(JSON.stringify(this.professionalExperienceList, null, 2));

                // Reset form
                this.professionalExperience = {
                    employment: '',
                    startDateProfessionalExperience: '',
                    endDateProfessionalExperience: '',
                    descriptionProfessionalExperience: '',
                    employerPosition: '',
                    nameOfEmployer: '',
                };

                this.isChecked = false;
            }
        },
        CreateApplicantProfessionalExperience: async function () {
            try {
                for (const proExp of this.professionalExperienceList) {
                    const result = await CreateApplicantProfessionalExperience(
                        this.companyId,
                        this.applicantId,
                        proExp.employment,
                        proExp.employerPosition,
                        proExp.nameOfEmployer,
                        proExp.startDateProfessionalExperience,
                        proExp.endDateProfessionalExperience || null,
                        proExp.descriptionProfessionalExperience
                    );
                    console.log("Professional Experience created successfully: " + result);
                }
            } catch (error) {
                console.error("Professional Experience creation failed:", error);
            }
        },
        deleteProfessionalExperience(index) {
            this.professionalExperienceList.splice(index, 1);
        },
        handleCertificateFile(file) {
            this.certificate.certificateFile = file;
        },
        handleResumeFileUpload(file) {
            this.resume.resumeFile = file;
        },
        CreateApplicantCertificate: async function () {
            try {
                for (const cert of this.certificateList) {
                    const result = await CreateApplicantCertificate(
                        this.companyId,
                        this.applicantId,
                        cert.certificateTypeId,
                        cert.unlistedCertificate ? cert.unlistedCertificate : 'none',
                        cert.startDateCertificate,
                        cert.endDateCertificate,
                        cert.descriptionCertificate,
                        cert.certificateType,
                        cert.certificateLink,
                        cert.certificateFile
                    );
                    console.log("Certificate created successfully: " + result);
                }
            } catch (error) {
                console.error("Certificate creation failed:", error);
            }
        },
        deleteCertificate(index) {
            this.certificateList.splice(index, 1);
        },
        addCertificate() {
            let valid = true;
            this.certSubmitted = true;

            this.unlistedCertificateIsValid = true;
            this.descriptionCertificateIsValid = true;

            // Certificate
            if (!this.certificate.certificateTypeId) {
                toastr.error(this.$Translate('certificateRequired'));
                this.certificateTypeIdIsValid = false;
                valid = false;
            } else {
                this.certificateTypeIdIsValid = true;
            }

            // Certificate Start Date
            if (!this.certificate.startDateCertificate) {
                toastr.error(this.$Translate('startDateCertificateRequired'));
                this.startDateCertificateIsValid = false;
                valid = false;
            } else {
                this.startDateCertificateIsValid = true;
            }

            // Certificate End Date
            if (!this.certificate.endDateCertificate) {
                toastr.error(this.$Translate('endDateCertificateRequired'));
                this.endDateCertificateIsValid = false;
                valid = false;
            } else {
                this.endDateCertificateIsValid = true;
            }

            // Radio 
            if (this.certificate.certificateType === 'file') {
                if (this.certificate.certificateFile === null) {
                    toastr.error(this.$Translate('certificateFileRequired'));
                    this.certificateUploadTypeIsValid = false;
                    valid = false;
                } else {
                    this.certificateUploadTypeIsValid = true;
                }
            } else if (this.certificate.certificateType === 'url') {
                if (!this.certificate.certificateLink) {
                    toastr.error(this.$Translate('certificateUrlRequired'));
                    this.certificateUploadTypeIsValid = false;
                    valid = false;
                } else {
                    this.certificateUploadTypeIsValid = true;
                }
            }


            if (valid) {
                // Add new certificate entry
                this.certificateList.push({ ...this.certificate });

                this.certSubmitted = false;

                //console.log(this.certificateList);

                // Reset form
                this.certificate = {
                    certificateTypeId: '',
                    unlistedCertificate: '',
                    startDateCertificate: '',
                    endDateCertificate: '',
                    descriptionCertificate: '',
                    certificateType: 'file',
                    certificateLink: '',
                    certificateFile: null
                };

                // Call child method to reset file input
                this.$refs.certificateForm.resetFileInput();
            }
        },
        deleteCertificate(index) {
            this.certificateList.splice(index, 1);
        },
        addCourse() {
            let valid = true;
            this.courseSubmitted = true;

            this.descriptionCourseIsValid = true;

            // Course Name
            if (!this.course.courseName) {
                toastr.error(this.$Translate('courseNameRequired'));
                this.courseNameIsValid = false;
                valid = false;
            } else {
                this.courseNameIsValid = true;
            }

            // Course Start Date
            if (!this.course.startDateCourse) {
                toastr.error(this.$Translate('startDateCourseRequired'));
                this.startDateCourseIsValid = false;
                valid = false;
            } else {
                this.startDateCourseIsValid = true;
            }

            // Course End Date
            if (!this.course.endDateCourse) {
                toastr.error(this.$Translate('endDateCourseRequired'));
                this.endDateCourseIsValid = false;
                valid = false;
            } else {
                this.endDateCourseIsValid = true;
            }

            if (valid) {
                // Add new course entry
                this.courseList.push({ ...this.course });

                this.courseSubmitted = false;

                //console.log('=== Courses ===');
                //console.log(JSON.stringify(this.course, null, 2));
                //console.log('=== Course List ===');
                //console.log(JSON.stringify(this.courseList, null, 2));

                // Reset form
                this.course = {
                    courseName: '',
                    startDateCourse: '',
                    endDateCourse: '',
                    descriptionCourse: ''
                };
            }
        },
        CreateApplicantCourse: async function () {
            try {
                for (const cou of this.courseList) {
                    const result = await CreateApplicantCourse(
                        this.companyId,
                        this.applicantId,
                        cou.courseName,
                        cou.startDateCourse,
                        cou.endDateCourse,
                        cou.descriptionCourse
                    );
                    console.log("Course created successfully: " + result);
                }
            } catch (error) {
                console.error("Course creation failed:", error);
            }
        },
        deleteCourse(index) {
            this.courseList.splice(index, 1);
        },
        addProjectExperience() {
            let valid = true;
            this.projExpSubmitted = true;

            this.descriptionProjectExperienceIsValid = true;

            // Role
            if (!this.projectExperience.role) {
                toastr.error(this.$Translate('roleRequired'));
                this.roleIsValid = false;
                valid = false;
            } else {
                this.roleIsValid = true;
            }

            // Project Experience Start Date
            if (!this.projectExperience.startDateProjectExperience) {
                toastr.error(this.$Translate('startDateProjectExperienceRequired'));
                this.startDateProjectExperienceIsValid = false;
                valid = false;
            } else {
                this.startDateProjectExperienceIsValid = true;
            }

            // Project Experience End Date
            if (!this.projectExperience.endDateProjectExperience) {
                toastr.error(this.$Translate('endDateProjectExperienceRequired'));
                this.endDateProjectExperienceIsValid = false;
                valid = false;
            } else {
                this.endDateProjectExperienceIsValid = true;
            }

            if (valid) {
                // Add new Project Experience entry
                this.projectExperienceList.push({ ...this.projectExperience });

                this.projExpSubmitted = false;

                //console.log('=== Project Experience ===');
                //console.log(JSON.stringify(this.projectExperience, null, 2));
                //console.log('=== Project Experience List ===');
                //console.log(JSON.stringify(this.projectExperienceList, null, 2));

                // Reset form
                this.projectExperience = {
                    role: '',
                    startDateProjectExperience: '',
                    endDateProjectExperience: '',
                    descriptionProjectExperience: ''
                };
            }
        },
        CreateApplicantProjectExperience: async function () {
            try {
                for (const projExp of this.projectExperienceList) {
                    const result = await CreateApplicantProjectExperience(
                        this.companyId,
                        this.applicantId,
                        projExp.role,
                        projExp.startDateProjectExperience,
                        projExp.endDateProjectExperience,
                        projExp.descriptionProjectExperience
                    );
                    console.log("Course created successfully: " + result);
                }
            } catch (error) {
                console.error("Course creation failed:", error);
            }
        },
        deleteProjectExperience(index) {
            this.projectExperienceList.splice(index, 1);
        },
        AddAttachment: async function () {
            try {
                const result = await AddAttachment(
                    this.companyId,
                    this.applicantId,
                    this.resume.resumeName,
                    this.resume.resumeType,
                    this.resume.resumeLink,
                    this.resume.resumeFile
                );
                console.log("Attachment added successfully: " + result);
            } catch (error) {
                console.error("Attachment creation failed:", error);
            }
        },
        async submitApplication() {
            this.resumeSubmitted = true;

            const isValid = await this.validateCurrentStep();

            if (isValid) {
                this.CreateApplicant();
            }
        },
        ApplicantLogin: async function () {
            try {
                const result = await ApplicantLogin(
                    this.companyId,
                    this.personalInfo.email,
                    this.personalInfo.password
                );
                SetToken(result.token);
                //console.log("Created Token:", GetToken());
                this.token = GetToken()
                SetApplicantId(result.applicantId);
                //console.log("Created Applicant ID:", GetApplicantId());
                this.applicantId = GetApplicantId();
                console.log("Created Applicant ID:" + this.applicantId);

            } catch (error) {
                console.error("Validation failed:", error);
            }
        },
        CreateApplicant: async function () {
            try {
                const result = await CreateApplicant(
                    this.companyId,
                    this.personalInfo.name,
                    this.personalInfo.arabicName,
                    this.personalInfo.nationalId,
                    this.personalInfo.countryRegionId,
                    this.personalInfo.gender,
                    this.personalInfo.dateOfBirth,
                    this.personalInfo.email,
                    this.personalInfo.confirmEmail,
                    this.personalInfo.phone,
                    this.personalInfo.password,
                    this.personalInfo.confirmPassword,
                    this.personalInfo.linkedInLink,
                    this.personalInfo.yearOfExperience
                );

                if (result && result.status === 200) {
                    //SetApplicantId(result.data);
                    //console.log("Created Applicant ID:", GetApplicantId());

                    this.resumeSubmitted = false;

                    this.$refs.resumeForm.resetResumeInput();

                    // COLLECT THE TOKEN HERE 
                    await this.ApplicantLogin();

                    // CALL THE CREATE FUNCTIONS ACCORDINGLY HERE (Needs token to be passed)
                    await this.CreateApplicantEducation();
                    await this.CreateApplicantSkill();
                    await this.CreateApplicantProfessionalExperience();
                    await this.CreateApplicantCertificate();
                    await this.CreateApplicantCourse();
                    await this.CreateApplicantProjectExperience();
                    await this.AddAttachment();

                    regSuccessModal.show();
                } else {
                    this.resumeSubmitted = true;
                }
            } catch (error) {
                console.error("Applicant creation failed:", error);
                regFailedModal.show();
            }
        },
        Restart() {
            this.$router.push({ name: 'Home' });
        },
        animateStep(direction) {
            const container = this.$refs.stepContainer;

            container.classList.remove('fade-in-right', 'fade-out-left', 'fade-in-left', 'fade-out-right');

            if (direction === 'next') {
                container.classList.add('fade-out-left');
                setTimeout(() => {
                    container.classList.remove('fade-out-left');
                    container.classList.add('fade-in-right');
                }, 300); // 
            } else {
                container.classList.add('fade-out-right');
                setTimeout(() => {
                    container.classList.remove('fade-out-right');
                    container.classList.add('fade-in-left');
                }, 300);
            }
        }
    },
    async mounted() {
        BlockPage(); // manually block once
        await runWithoutBlocking(async () => {
            await Promise.all([this.GetGenders(), this.GetCountries(), this.GetEducationLevel(), this.GetEducation(), this.GetSkill(), this.GetCertificate()]);
        });
        UnBlockPage();

        regSuccessModal = new Modal(document.getElementById("regSuccessModal"), {});
        regFailedModal = new Modal(document.getElementById("regFailedModal"), {});
    },
    computed: {
        progressWidth() {
            return `${(this.currentStep / (this.steps.length - 1)) * 100}%`;
        },
    },
    watch: {
        'skill.skillId'(newSkillId) {
            const selectedSkill = this.skillsList.find(sk => sk.skillId === newSkillId);
            this.skill.recId = selectedSkill ? selectedSkill.recId : null;
            this.skill.description = selectedSkill ? selectedSkill.description : null;
        },
        'skill.ratingLevelId'(newRatingId) {
            const selectedRating = this.ratingLevelList.find(rt => rt.recId === newRatingId);
            this.skill.ratingLevelDescription = selectedRating?.description || null;
        },
        'certificate.certificateTypeId'(newCertId) {
            const selectedCert = this.certificatesList.find(cert => cert.recId === newCertId);
            this.certificate.certificateName = selectedCert ? selectedCert.description : null;
        },
        'education.educationLevel'(newEduId) {
            const selectedEdu = this.educationalLevelsList.find(edu => edu.recId === newEduId);
            this.education.educationalLevelDesc = selectedEdu?.description || null;
        }
    }

};
</script>

<style scoped>
/* You can add custom styles for this component here */
.mainContainer {
    background-image: url('../../../../dist/assets/RegistrationBackground.jpg');
    background-size: cover;
    background-position: top;
    height: 115vh auto;
}

.navbar-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    position: relative;
    padding-top: 10px;
    margin: 20px auto;
}

.navbar-progress {
    position: absolute;
    top: 40px;
    background-color: #ccc;
    height: 3px;
    z-index: 0;
    left: 5%;
    width: 88%;
}

.navbar-fill {
    height: 100%;
    background-color: #00013A;
    transition: width 0.4s ease-in-out;
}

/* Step Styling */
.step {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    min-width: 80px;
}

.step p {
    margin-top: 8px;
    font-size: 16px;
    color: #00013A;
    text-align: center;
    white-space: nowrap;
}

/* Step Circle */
.step .circle {
    font-size: 22px;
    color: #ccc;
    width: 60px;
    height: 60px;
    background-color: white;
    border-radius: 50%;
    border: 3px solid #ccc;
    align-items: center;
    justify-content: center;
    display: inline-flex;
}

/* Active Step */
.step.active .circle {
    color: #00013A;
    background-color: white;
    border: 3px solid #00013A;
}

.step.active p {
    font-weight: bold;
}

/* Completed Step */
.step.completed .circle {
    color: white;
    background-color: #00013A;
    border: 3px solid #00013A;
}

.step.completed p {
    font-weight: 400;
}

.form-tab {
    background-color: #F8F8F8;
    width: 80%;
    border-radius: 10px;
    border: 1px solid #DFE1E3;
    align-items: center;
    justify-content: center;
    margin: 20px auto;
    text-align: center;
    padding: 20px;
}

.next-btn,
.back-btn {
    padding: 10px 20px;
    border-radius: 10px;
    width: 150px;
    font-weight: 500;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
}

.next-btn {
    background-color: #00013A;
    border: 1px solid #00013A;
    color: white;
}

.back-btn {
    background-color: white;
    border: 1px solid #00013A;
    color: #00013A;
}

.add-btn {
    padding: 10px 20px;
    border-radius: 10px;
    width: 250px;
    font-weight: 500;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
}

.add-btn {
    background-color: white;
    border: 1px solid #00013A;
    color: #00013A;
}

.add-btn:hover {
    background-color: #00013A;
    border: 1px solid white;
    color: white;
}

.col-md-4 {
    text-align: start;
}

.col-md-4 label {
    margin-bottom: 10px;
}

.col-md-4 input,
.col-md-4 select,
.col-md-4 textarea {
    margin-bottom: 12px;
}

.col-md-4 select {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background: url('../../../../dist/assets/select.svg') no-repeat right 0.75rem center;
    background-color: #fff;
    background-size: 16px 16px;
    padding-right: 2rem;
    line-height: 1.5;
    height: calc(1.5em + 0.75rem + 2px);
}

.fileArea,
.urlArea {
    margin-top: 2px;
    width: 100%;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 10px;
    text-align: start;
}

@keyframes fadeOutLeft {
    from {
        opacity: 1;
        transform: translateX(0);
    }

    to {
        opacity: 0;
        transform: translateX(-170px);
    }
}

@keyframes fadeInRight {
    from {
        opacity: 0;
        transform: translateX(170px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes fadeOutRight {
    from {
        opacity: 1;
        transform: translateX(0);
    }

    to {
        opacity: 0;
        transform: translateX(170px);
    }
}

@keyframes fadeInLeft {
    from {
        opacity: 0;
        transform: translateX(-170px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

.fade-in-right {
    animation: fadeInRight 0.5s ease forwards;
}

.fade-out-left {
    animation: fadeOutLeft 0.5s ease forwards;
}

.fade-in-left {
    animation: fadeInLeft 0.5s ease forwards;
}

.fade-out-right {
    animation: fadeOutRight 0.5s ease forwards;
}
</style>